import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Observable} from 'rxjs';
import {mapParseItem, mapParseList} from '@brunata-angular/util';
import {environment} from 'src/environments/environment';
import {
    InstallationProject,
    InstallationProjectDetail,
    installationProjectDetailSchema,
    InstallationProjectLocation,
    installationProjectLocationSchema,
    installationProjectSchema,
} from './installation-projects.model';

@Injectable({
    providedIn: 'root',
})
export class InstallationProjectsService {
    private readonly http = inject(HttpClient);

    /** All installation projects the signed-in caller may see - the backend scopes to the
     * caller's customer (administrators) or granted buildings (sub-users). */
    getInstallationProjects(): Observable<InstallationProject[]> {
        return this.http
            .get(`${environment.onlineServiceUrl}/installationprojects`)
            .pipe(mapParseList(installationProjectSchema));
    }

    /** One project's overview. The backend answers 404 for a project the caller may not see. */
    getInstallationProject(projectId: string): Observable<InstallationProjectDetail> {
        return this.http
            .get(
                `${environment.onlineServiceUrl}/installationprojects/` +
                    encodeURIComponent(projectId),
            )
            .pipe(mapParseItem(installationProjectDetailSchema));
    }

    /** One project's locations and their visits, sorted by visit date ascending with unscheduled
     * locations last. The backend answers 404 for a project the caller may not see. */
    getInstallationProjectLocations(projectId: string): Observable<InstallationProjectLocation[]> {
        return this.http
            .get(
                `${environment.onlineServiceUrl}/installationprojects/` +
                    `${encodeURIComponent(projectId)}/locations`,
            )
            .pipe(mapParseList(installationProjectLocationSchema));
    }
}

/** Whether a failed call was the backend's 404 - an unknown, foreign or malformed project id. */
export function isNotFound(failure: unknown): boolean {
    return failure instanceof HttpErrorResponse && failure.status === 404;
}
